// The coach's one setting: whether the page connects to the local bridge,
// as `settings/coach.json`. Off by default: the user (2026-10-21) asked for
// "the slider in the settings page" instead of a page that dials on its own.
// Pure parsing, and a small store over the storage API that the entry point
// reads and the Settings section writes.
import type { Storage } from '@chess/api-storage';

export const COACH_FILE = 'coach.json';

export interface CoachSettings {
  enabled: boolean;
}

export const DEFAULT_COACH_SETTINGS: CoachSettings = { enabled: false };

/** The settings from the file's text; the defaults for no file or a broken one. */
export function parseCoachSettings(text: string | undefined): CoachSettings {
  if (!text) return { ...DEFAULT_COACH_SETTINGS };
  try {
    const raw: unknown = JSON.parse(text);
    if (typeof raw !== 'object' || raw === null) return { ...DEFAULT_COACH_SETTINGS };
    const enabled = (raw as { enabled?: unknown }).enabled;
    return { enabled: typeof enabled === 'boolean' ? enabled : DEFAULT_COACH_SETTINGS.enabled };
  } catch {
    return { ...DEFAULT_COACH_SETTINGS };
  }
}

export type CoachStorage = Pick<Storage, 'readText' | 'write'>;

export class CoachSettingsStore {
  private current: CoachSettings = { ...DEFAULT_COACH_SETTINGS };
  private loaded?: Promise<CoachSettings>;
  private listeners = new Set<(s: CoachSettings) => void>();
  private readonly storage: Promise<CoachStorage>;

  constructor(storage: Promise<CoachStorage>) {
    this.storage = storage;
  }

  /** The settings as last read or saved; the defaults until `load` resolves. */
  get(): CoachSettings {
    return this.current;
  }

  /** Reads the file once; later calls return the same promise. */
  load(): Promise<CoachSettings> {
    this.loaded ??= this.storage
      .then((s) => s.readText(COACH_FILE))
      .then((text) => this.set(parseCoachSettings(text)));
    return this.loaded;
  }

  async save(next: CoachSettings): Promise<CoachSettings> {
    const s = await this.storage;
    await s.write(COACH_FILE, JSON.stringify(next));
    return this.set(next);
  }

  subscribe(listener: (s: CoachSettings) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private set(next: CoachSettings): CoachSettings {
    this.current = next;
    for (const l of this.listeners) l(next);
    return next;
  }
}
